import jojoAccount from '@jojo/account-sdk';
import { Toast } from '@jojo-design/mobile';
import { customError } from '@woulsl/sentry-config';
import qs from 'query-string';
import { Cookies } from 'react-cookie';
import router from 'umi/router';

import { IS_NEW_ACCOUNT_AUTH } from '@/config/const';

import base, { DEBUG_LOCAL } from '../config/base';
import { getClassIdsByLinkCode } from '../services/coupon';
import {
  isJoJoAllBrowser,
  isLoginInBrowser,
  isLoginInWxBrowser,
  isWXAppBrowser,
  isWXBrowser,
  isWXMiniAppBrowser
} from './util';

const { AUTH_SIGN_URL } = base;
const cookies = new Cookies();

type AuthorizeType = {
  appId?: string; //
  mode?: number; // 1 登录， 2 不登录
  wechatAuthType?: number; // 1 用户授权 2 静默授权
  authBizType?: 3 | 4; // 3 双账号处理场景 4 微信授权
  linkCode?: string; // 用于查询班期id
  requestUrl?: string; // 用于查询班期id
};

/**
 * 根据linkCode获取班期id
 * @param linkCode
 * @returns
 */
export const _getClassIdsByLinkCode = async () => {
  const params = new URLSearchParams(window.location.search);
  const linkCode = params.get('linkCode'); // 获取参数 linkCode 的值
  const activityId = params.get('activityId'); // 获取参数 activityId 的值

  let classIdStr = '';
  // 仅微信浏览器环境，促销链路需要获取班期id
  if (isWXAppBrowser() && (linkCode?.startsWith('NL') || activityId?.startsWith('B'))) {
    try {
      const res = await getClassIdsByLinkCode({ linkCode: linkCode || activityId });
      const classIds = res?.data?.map((item: any) => item?.id) || [];
      customError({
        name: '班期id长度',
        message: '班期id长度',
        captureContext: {
          level: 'info',
          extra: {
            href: window.location.href,
            classIdsLen: classIds?.length
          }
        }
      });

      classIdStr = classIds?.length ? String(classIds) : '';
    } catch (error) {
      customError({
        name: '获取班期id失败',
        message: '获取班期id失败',
        captureContext: {
          level: 'error',
          extra: {
            href: window.location.href,
            linkCode
          }
        }
      });
    }
  }
  localStorage.setItem('classIdStr', classIdStr);
};

export function toAuthrize({
  appId,
  mode,
  wechatAuthType,
  authBizType,
  requestUrl
}: AuthorizeType) {
  // 跳转参数拼接
  const url = requestUrl || window.location.href;
  // 重定向地址添加type类型
  const authWechatAppId = appId ? `authWechatAppId=${appId}&` : '';
  const modeStr = mode ? `mode=${mode}&` : '';
  const wechatAuthTypeStr = wechatAuthType ? `wechatAuthType=${wechatAuthType}&` : '';
  const authBizTypeStr = authBizType ? `authBizType=${authBizType}&` : '';
  // 获取班期id
  const classIds = localStorage.getItem('classIdStr') || '';
  const classIdStr = classIds ? `dubbleAccountLoginClassIds=${classIds}&` : '';

  const redirectUrl = [
    AUTH_SIGN_URL,
    '?',
    modeStr,
    authWechatAppId,
    wechatAuthTypeStr,
    authBizTypeStr,
    classIdStr,
    'requestUrl=',
    encodeURIComponent(url)
  ].join('');
  return redirectUrl;
}

export function needNewAuth() {
  const authToken = cookies.get('authToken');
  const isNewAccountAuth = cookies.get(IS_NEW_ACCOUNT_AUTH);
  // 在微信浏览器环境内，已登录非新授权，唤起新授权
  if (isWXAppBrowser() && authToken && isNewAccountAuth !== 'yes') {
    setTimeout(() => {
      const redirectUrl = toAuthrize({
        mode: 1,
        authBizType: 3
      });
      window.location.replace(redirectUrl);
    }, 100);

    return true;
  }
  return false;
}

/**
 * 检测是否是双账号登录
 * @returns {boolean}
 */
export function isDoubleAccountLogin() {
  const authToken = cookies.get('authToken');
  const isNewAccountAuth = cookies.get(IS_NEW_ACCOUNT_AUTH);
  // 在微信浏览器环境内，已登录非新授权，唤起新授权
  if (isWXAppBrowser() && authToken && isNewAccountAuth === 'yes') {
    return true;
  }
  return false;
}

interface WxAuthOptions {
  needPopLogin?: boolean; // 是否需要弹出登录，该参数仅用于给链接上添加needPopLogin参数，后续弹窗登录逻辑需单独处理
}
/**
 * 微信授权: 用于进授权不登录
 */
export const wxAuth = (props?: WxAuthOptions) => {
  // 非微信浏览器环境，不进行微信授权
  if (!isWXAppBrowser() || DEBUG_LOCAL) {
    return true;
  }
  const { needPopLogin = false } = props || {};

  // 已授权，不进行微信授权
  const wxAuthCode = cookies.get('uc_dubbleAccount_wxAuthCode');
  if (wxAuthCode) {
    return true;
  }

  // 仅授权时，已有登录态，不进行微信授权
  if (!needPopLogin) {
    const _isDoubleAccountLogin = isDoubleAccountLogin();
    if (_isDoubleAccountLogin) {
      return true;
    }
  }

  // 未授权，进行微信授权
  let url = window.location.href;
  if (needPopLogin) {
    const [baseUrl, queryString] = url.split('?');
    const existingParams = qs.parse(queryString);
    const mergedParams = { ...existingParams, needPopLogin: true };
    const newQueryString = qs.stringify(mergedParams);
    url = `${baseUrl}?${newQueryString}`;
  }
  const authUrl = toAuthrize({
    mode: 1,
    authBizType: 4,
    requestUrl: url
  });
  window.location.replace(authUrl);
  return false;
};

/**
 * 清除链接上的 needPopLogin 参数
 */
const clearNeedPopLoginInUrl = () => {
  const existingParams = qs.parse(window.location.search.replace(/^\?/, ''));
  const { needPopLogin, ...rest } = existingParams || {};
  const url = `${window.location.pathname?.replace('/mall/center', '')}?${qs.stringify(rest)}`;
  router.replace(url);
};

/**
 * 添加链接上的 needPopLogin 参数
 */
const addNeedPopLoginInUrl = () => {
  const existingParams = qs.parse(window.location.search.replace(/^\?/, ''));
  const newQuery = {
    ...existingParams,
    needPopLogin: true
  };
  const curUrl = `${window.location.pathname?.replace('/mall/center', '')}?${qs.stringify(
    newQuery
  )}`;
  router.replace(curUrl);
};

interface PopLoginOptions {
  callback?: () => void;
}
/**
 * 弹窗登录
 */
export const popLogin = (props?: PopLoginOptions) => {
  if (isJoJoAllBrowser() || isWXMiniAppBrowser()) {
    return;
  }

  if (isWXAppBrowser()) {
    // 先微信授权
    const isWxAuth = wxAuth({ needPopLogin: true });
    if (!isWxAuth) {
      return;
    }
  }

  const { callback } = props || {};

  // 弹出弹窗，清除链接上的标识，避免刷新页面再次弹出
  clearNeedPopLoginInUrl();

  jojoAccount.popupLogin({
    wechatLogin: {
      mode: 1,
      wechatAuthType: 1,
      requestUrl: window.location.href
    },
    showWechatBtn: 'hidden',
    ...(isWXAppBrowser() ? { dubbleAccountLoginVersion: 'V2' } : {}),
    dubbleAccountLoginClassIds: localStorage.getItem('classIdStr') || '',
    dubbleAccountLogin: true,
    onAgreementRedirect: (url: string) => {
      addNeedPopLoginInUrl();
      window.location.href = url;
    },
    callback: () => {
      callback?.();
    },
    onError: (res) => {
      // 微信授权code失效，重新授权
      if (String(res?.subCode) === '3033') {
        wxAuth({ needPopLogin: true });
      }
    }
  });
};

/**
 * 是否登录
 */
export const isLogin = () => {
  // 本地调试走鉴权接口，本地会模拟登录
  if (DEBUG_LOCAL) {
    return true;
  }
  // 微信浏览器
  if (isWXAppBrowser()) {
    return isLoginInWxBrowser();
  }
  // 普通浏览器（排除 微信内环境 和 APP环境）
  if (!isWXBrowser() && !isJoJoAllBrowser()) {
    return isLoginInBrowser();
  }
  // 其他环境默认走鉴权接口
  return true;
};

/**
 * 店铺是否登录
 */
export const isShopLogin = () => {
  // 本地调试走鉴权接口，本地会模拟登录
  if (DEBUG_LOCAL) {
    return true;
  }
  // 非app环境可展示非登录态
  if (!isJoJoAllBrowser()) {
    return !!cookies.get('authToken');
  }
  // app环境默认走鉴权接口
  return true;
};

/* 账号比对
 * 用于外部系统跳转商城页面时，将链接上的verifyUid与当前登录用户uid进行比对，如不是同一个用户，弹出更换登录账号提示
 */
export function comparisonAccount() {
  const params = qs.parse(window.location.search, {
    decode: false
  });
  const { verifyUid } = params || {};
  if (!verifyUid) {
    return;
  }
  jojoAccount.popupAccountCompare({
    verifyUid: decodeURIComponent(verifyUid),
    loginOutCallback: () => {
      const redirectUrl = toAuthrize({
        mode: 1,
        authBizType: 3
      });
      window.location.replace(redirectUrl);
    }
  });
}

enum AccountState {
  NOT_APPLY = 'NOT_APPLY', // 未申请注销
  WAIT_CANCELLATION = 'WAIT_CANCELLATION', // 等待注销
  CANCELLATION = 'CANCELLATION', // 已注销
  CANCELED = 'CANCELED' // 已取消
}
/*
 * 校验用户是否申请注销
 */
export async function popupLogout(props: any = {}) {
  let isShowLogout = false;
  try {
    const res = await jojoAccount.popupLogout({
      confirmCallback: () => {
        Toast.show({
          content: '撤销成功',
          icon: 'success'
        });
      },
      ...props
    });
    const { status, data } = res || {};
    if (status === 0) {
      const { accountState } = data || {};
      if (accountState === AccountState.WAIT_CANCELLATION) {
        isShowLogout = true;
      }
    }
  } catch (error) {
    console.log(error);
  }
  return isShowLogout;
}
